import { ClearBaseIcon } from '@tableau/icons/ClearBaseIcon';
import { NotificationCheckIcon } from '@tableau/icons/NotificationCheckIcon';
import { CheckBoxWidget } from '@tableau/widgets';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { classes } from 'typestyle';

import { Messages } from '~src/codegen/Localize';

import { AdminProfileDialogStyles } from '../styles/AdminProfileDialogStyles';

const CATEGORY_ORDER = ['User', 'Group'];

function getCapabilityText(
  resource: string,
  capability: string,
  type: 'label' | 'description' | 'hint'
): string | undefined {
  const resourceKey = resource.replace(/-/g, '');
  const key = `adminProfile_${resourceKey}_${capability}_${type}`;
  const messageFn = Messages[key as keyof typeof Messages] as (() => string) | undefined;
  return typeof messageFn === 'function' ? messageFn() : undefined;
}

function getCapabilityLabel(resource: string, capability: string): string {
  return getCapabilityText(resource, capability, 'label') || capability;
}

function getCategoryDisplayName(category: string): string {
  const categoryKey = category.replace(/-/g, '');
  const key = `adminProfile_${categoryKey}_category`;
  const messageFn = Messages[key as keyof typeof Messages] as (() => string) | undefined;
  return typeof messageFn === 'function' ? messageFn() : category;
}

function getOrderedCategories(permissions: Record<string, string[]>): string[] {
  const allCategories = Object.keys(permissions);
  const ordered: string[] = [];
  CATEGORY_ORDER.forEach(c => {
    if (allCategories.indexOf(c) !== -1) {
      ordered.push(c);
    }
  });
  allCategories.forEach(c => {
    if (ordered.indexOf(c) === -1) {
      ordered.push(c);
    }
  });
  return ordered;
}

export interface AdminProfileCapabilitiesListProps {
  permissions: Record<string, string[]>;
  isCapabilityEnabled: (category: string, capability: string) => boolean;
  readOnly?: boolean;
  onToggleCapability?: (category: string, capability: string, checked: boolean) => void;
  onSelectAll?: (category: string, capabilities: string[]) => void;
  disabled?: boolean;
  testIdPrefix?: string;
  containerClassName?: string;
  showDescription?: boolean;
}

export const AdminProfileCapabilitiesList: React.FC<AdminProfileCapabilitiesListProps> = ({
  permissions,
  isCapabilityEnabled,
  readOnly = false,
  onToggleCapability,
  onSelectAll,
  disabled = false,
  testIdPrefix = 'capability-hint',
  containerClassName,
  showDescription = true,
}) => {
  const orderedCategories = React.useMemo(
    () => getOrderedCategories(permissions),
    [permissions]
  );

  const renderCapabilityContent = (
    label: string,
    description: string | undefined,
    hint: string | undefined,
    category: string,
    capability: string
  ) => (
    <div className={AdminProfileDialogStyles.CapabilityContent}>
      <span className={AdminProfileDialogStyles.CapabilityLabel}>{label}</span>
      {description && (
        <div className={`${AdminProfileDialogStyles.CapabilityDescription} ${AdminProfileDialogStyles.CapabilityMarkdown}`}>
          <ReactMarkdown source={description} />
        </div>
      )}
      {hint && (
        <div
          className={AdminProfileDialogStyles.CapabilityHint}
          data-tb-test-id={`${testIdPrefix}-${category}-${capability}`}
        >
          <ReactMarkdown source={hint} />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={classes(
        AdminProfileDialogStyles.PermissionsContainer,
        containerClassName
      )}
    >
      {orderedCategories.map(category => {
        const capabilities = permissions[category];
        const enabledCount = capabilities.filter(c =>
          isCapabilityEnabled(category, c)
        ).length;
        const totalCount = capabilities.length;
        const displayName = getCategoryDisplayName(category);

        return (
          <div key={category}>
            <div className={AdminProfileDialogStyles.CategoryHeader}>
              <div>
                <span className={AdminProfileDialogStyles.CategoryTitle}>
                  {displayName}
                </span>
                <span className={AdminProfileDialogStyles.CategoryCount}>
                  {readOnly
                    ? Messages.adminProfilePermissionsCapabilitiesCount({ COUNT: String(enabledCount) })
                    : Messages.createAdminProfileCapabilitiesEnabled({
                      SELECTED: String(enabledCount),
                      TOTAL: String(totalCount),
                    })
                  }
                </span>
              </div>
              {!readOnly && onSelectAll && (
                <button
                  type="button"
                  className={AdminProfileDialogStyles.SelectAll}
                  onClick={() => onSelectAll(category, capabilities)}
                  disabled={disabled}
                  data-tb-test-id={`select-all-${category}`}
                >
                  {Messages.createAdminProfileSelectAll()}
                </button>
              )}
            </div>

            <div className={AdminProfileDialogStyles.CheckboxList}>
              {capabilities.map(capability => {
                const label = getCapabilityLabel(category, capability);
                const description = showDescription ? getCapabilityText(category, capability, 'description') : undefined;
                const hint = getCapabilityText(category, capability, 'hint');
                const checkboxId = `permission-${category}-${capability}`;
                const enabled = isCapabilityEnabled(category, capability);

                if (readOnly) {
                  return (
                    <div
                      key={capability}
                      className={AdminProfileDialogStyles.CapabilityItem}
                      style={{ cursor: 'default' }}
                    >
                      {enabled ? (
                        <NotificationCheckIcon
                          size={AdminProfileDialogStyles.CapabilityIconEnabled.size}
                          color={AdminProfileDialogStyles.CapabilityIconEnabled.color}
                        />
                      ) : (
                        <ClearBaseIcon
                          size={AdminProfileDialogStyles.CapabilityIconDisabled.size}
                          color={AdminProfileDialogStyles.CapabilityIconDisabled.color}
                        />
                      )}
                      {renderCapabilityContent(label, description, hint, category, capability)}
                    </div>
                  );
                }

                return (
                  <label
                    key={capability}
                    htmlFor={checkboxId}
                    className={AdminProfileDialogStyles.CapabilityItem}
                  >
                    <CheckBoxWidget
                      id={checkboxId}
                      checked={enabled}
                      handleChange={(checked: boolean) =>
                        onToggleCapability?.(category, capability, checked)
                      }
                      testId={checkboxId}
                      disabled={disabled}
                    />
                    {renderCapabilityContent(label, description, hint, category, capability)}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

AdminProfileCapabilitiesList.displayName = 'AdminProfileCapabilitiesList';
